import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {TransitionDirection} from '@Panels/transitionable-panel-v2/transitionable-panel-v2.types';
import type {RootState} from '@/store';
import type {
  CheckoutBillingAddress,
  CheckoutConfirmationData,
  CheckoutDialogData,
  CheckoutFlowState,
  CheckoutMode,
  CheckoutPollingIds,
  CheckoutStripeSession,
  GoToStepPayload,
  IosCreditPackPrice,
} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutStep, POPULAR_CREDIT_PACK_QUANTITY} from '@Components/checkout-flow/checkout-flow.types';

const getInitialPromoState = (code: string): CheckoutFlowState['promo'] => {
  return {code, isApplied: code !== '', appliedText: '', errorMessage: ''};
};

const getInitialPollingIds = (): CheckoutPollingIds => {
  return {invoiceId: '', chargeId: '', paymentIntentId: '', setupIntentId: ''};
};

const initialState: CheckoutFlowState = {
  isActive: false,
  mode: null,
  step: CheckoutStep.PREFLIGHT,
  transitionDirection: TransitionDirection.FORWARD,
  isLoading: true,
  dialogData: null,
  selectedCreditPackPriceId: null,
  iosCreditPackPrices: {},
  promo: getInitialPromoState(''),
  addresses: [],
  selectedBillingAddressId: null,
  stripeSession: null,
  pollingIds: getInitialPollingIds(),
  confirmation: null,
  isCreditBenefitsViewOpen: false,
  errorTitle: '',
  errorMessage: '',
};

interface StartCheckoutFlowPayload {
  mode: CheckoutMode;
  promoCode: string;
}

interface SetCheckoutErrorPayload {
  title: string;
  message: string;
}

export const checkoutFlowSlice = createSlice({
  name: 'checkoutFlow',
  initialState,
  reducers: {
    startCheckoutFlow: (state, {payload}: PayloadAction<StartCheckoutFlowPayload>) => {
      Object.assign(state, initialState);
      state.isActive = true;
      state.mode = payload.mode;
      state.promo = getInitialPromoState(payload.promoCode);
    },
    setEffectiveCheckoutMode: (state, {payload}: PayloadAction<CheckoutMode>) => {
      state.mode = payload;
    },
    goToCheckoutStep: (state, {payload}: PayloadAction<GoToStepPayload>) => {
      state.step = payload.step;
      state.transitionDirection = payload.direction;
      state.isCreditBenefitsViewOpen = false;
    },
    setCheckoutLoading: (state, {payload}: PayloadAction<boolean>) => {
      state.isLoading = payload;
    },
    setCheckoutDialogData: (state, {payload}: PayloadAction<CheckoutDialogData>) => {
      state.dialogData = payload;
      state.addresses = payload.addresses;
      const preselectedCreditPack = payload.creditPacks.find((creditPack) => creditPack.quantity === POPULAR_CREDIT_PACK_QUANTITY) ?? payload.creditPacks[0];
      state.selectedCreditPackPriceId = preselectedCreditPack === undefined ? null : preselectedCreditPack.priceId;
    },
    setCreditBenefitsViewOpen: (state, {payload}: PayloadAction<boolean>) => {
      state.isCreditBenefitsViewOpen = payload;
      state.transitionDirection = payload ? TransitionDirection.FORWARD : TransitionDirection.BACKWARD;
    },
    selectCreditPack: (state, {payload}: PayloadAction<number | null>) => {
      state.selectedCreditPackPriceId = payload;
      state.stripeSession = null;
    },
    setIosCreditPackPrices: (state, {payload}: PayloadAction<Record<number, IosCreditPackPrice>>) => {
      state.iosCreditPackPrices = payload;
    },
    setPromoCode: (state, {payload}: PayloadAction<string>) => {
      state.promo.code = payload;
      state.promo.errorMessage = '';
    },
    setPromoApplied: (state, {payload}: PayloadAction<string>) => {
      state.promo.isApplied = true;
      state.promo.appliedText = payload;
      state.promo.errorMessage = '';
    },
    setPromoError: (state, {payload}: PayloadAction<string>) => {
      state.promo.code = '';
      state.promo.errorMessage = payload;
    },
    clearPromoError: (state) => {
      state.promo.errorMessage = '';
    },
    addBillingAddress: (state, {payload}: PayloadAction<CheckoutBillingAddress>) => {
      state.addresses.unshift(payload);
      state.selectedBillingAddressId = payload.id;
    },
    removeBillingAddress: (state, {payload}: PayloadAction<number>) => {
      state.addresses = state.addresses.filter((address) => address.id !== payload);
      if (state.selectedBillingAddressId === payload) {
        state.selectedBillingAddressId = null;
      }
    },
    selectBillingAddress: (state, {payload}: PayloadAction<number>) => {
      state.selectedBillingAddressId = payload;
    },
    setStripeSession: (state, {payload}: PayloadAction<CheckoutStripeSession | null>) => {
      state.stripeSession = payload;
    },
    setPollingIds: (state, {payload}: PayloadAction<CheckoutPollingIds>) => {
      state.pollingIds = payload;
    },
    setCheckoutConfirmation: (state, {payload}: PayloadAction<CheckoutConfirmationData | null>) => {
      state.confirmation = payload;
    },
    setCheckoutError: (state, {payload}: PayloadAction<SetCheckoutErrorPayload>) => {
      state.errorTitle = payload.title;
      state.errorMessage = payload.message;
      state.isLoading = false;
      state.step = CheckoutStep.ERROR;
      state.transitionDirection = TransitionDirection.FORWARD;
    },
    resetCheckoutFlow: () => {
      return initialState;
    },
  },
});

export const {
  startCheckoutFlow,
  setEffectiveCheckoutMode,
  goToCheckoutStep,
  setCheckoutLoading,
  setCheckoutDialogData,
  setCreditBenefitsViewOpen,
  selectCreditPack,
  setIosCreditPackPrices,
  setPromoCode,
  setPromoApplied,
  setPromoError,
  clearPromoError,
  addBillingAddress,
  removeBillingAddress,
  selectBillingAddress,
  setStripeSession,
  setPollingIds,
  setCheckoutConfirmation,
  setCheckoutError,
  resetCheckoutFlow,
} = checkoutFlowSlice.actions;

export const checkoutFlowReducer = checkoutFlowSlice.reducer;

export const selectCheckoutFlow = (state: RootState): CheckoutFlowState => {
  return state.checkoutFlow;
};

export const selectIsCheckoutFlowActive = (state: RootState): boolean => {
  return state.checkoutFlow.isActive;
};

export const selectCheckoutStep = (state: RootState): CheckoutStep => {
  return state.checkoutFlow.step;
};
